// The access matrix, as the website sees it (backend ACCESS_MATRIX.md).
//
// The server decides every request; this only decides what to SHOW. What
// a person may open comes from GET /api/access/me. Until that answer
// arrives (the first moment after signing in, or an older server), the
// role's defaults below stand in — they mirror the backend's DEFAULTS, so
// nothing flickers in or out when the real answer lands.

export type AccessLevel = 'none' | 'view' | 'edit' | 'manage';
export type AccessScope = 'area' | 'company';
export type AccessArea =
  | 'payroll' | 'reports' | 'hiring' | 'hiringBackOffice' | 'hiringSetup' | 'people'
  | 'files' | 'restrictedFiles' | 'fleet' | 'fleetCost' | 'accidents' | 'operations'
  | 'tasks' | 'audit' | 'companySettings' | 'access';
export type AccessCell = { level: AccessLevel; scope?: AccessScope };
export type AccessMap = Record<AccessArea, AccessCell>;

export const LEVELS: AccessLevel[] = ['none', 'view', 'edit', 'manage'];
export const atLeast = (have: AccessLevel | undefined, need: AccessLevel) =>
  LEVELS.indexOf(have || 'none') >= LEVELS.indexOf(need);

const ALL: AccessArea[] = [
  'payroll', 'reports', 'hiring', 'hiringBackOffice', 'hiringSetup', 'people',
  'files', 'restrictedFiles', 'fleet', 'fleetCost', 'accidents', 'operations',
  'tasks', 'audit', 'companySettings', 'access',
];
const fill = (level: AccessLevel, cells: Partial<Record<AccessArea, AccessCell>> = {}): AccessMap =>
  Object.fromEntries(ALL.map((a) => [a, cells[a] || { level }])) as AccessMap;

const A = (scope: AccessScope = 'company') => (level: AccessLevel): AccessCell => ({ level, scope });
const co = A();
const area = A('area');

/** The role's defaults — the stand-in until the server's answer arrives. */
export function defaultAccess(role: string | undefined): AccessMap {
  switch (role) {
    case 'AO':
      return fill('manage', { audit: co('view'), restrictedFiles: co('view') });
    case 'CA':
      return fill('manage', { audit: co('view'), restrictedFiles: co('view'), access: co('none') });
    case 'BC':
      return fill('none', {
        hiring: area('edit'), hiringSetup: co('view'), people: co('edit'), files: area('edit'),
        fleet: area('manage'), accidents: area('edit'), operations: area('edit'), tasks: co('edit'),
        companySettings: co('view'),
      });
    case 'Driver':
      return fill('none', { files: co('view'), fleet: co('view'), accidents: co('view'), tasks: co('view') });
    case 'Maintainer':
      return fill('none', { files: co('edit'), fleet: co('edit'), tasks: co('view') });
    default:
      return fill('none');
  }
}
